import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileService } from '@univacity/angular/datas';
import {
  APPLICATION_DOCUMENT_TYPE,
  BUCKET,
  File,
} from '@univacity/shared/types';
import { ObjectUtil, StringUtil } from '@univacity/shared/util';
import { NzUploadChangeParam, NzUploadFile } from 'ng-zorro-antd/upload';
import { Configuration } from '../../configuration';
import { Size } from '../../interfaces';

/* eslint-disable @typescript-eslint/no-empty-function */

interface InputFile
  extends Partial<Pick<File, '_id' | 'size' | 'mimetype' | 'documentType'>> {
  src: string;
  name: string;
}
const getBase64 = (file: any): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
@Component({
  selector: 'un-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class FileInputComponent implements ControlValueAccessor, OnInit {
  @Input() disabled?: boolean;
  @Input() maxLength?: number;
  @Input() multiple?: boolean;
  @Input() format?: 'default' | 'large' = 'default';
  @Input() size: Size = 'medium';
  @Input() bucket: BUCKET = BUCKET.private;
  @Input() accept?: string;
  @Input() recommendedSize?: [number, number];
  @Input() fileValue?: 'full' | 'src' = 'src';
  @Input() documentType?: APPLICATION_DOCUMENT_TYPE;
  @Input() documentTypes = ObjectUtil.enumToKeyLabelValuePair(
    APPLICATION_DOCUMENT_TYPE
  );
  private uploadUrl: string;
  @Input() uploadType: 'drag' | 'photo' | 'icon' = 'drag';
  @Input() rounded?: boolean;
  previewVisible: boolean;
  fileList: Map<string, InputFile>;
  previewImage: string;
  list: NzUploadFile[] = [];

  propagateChange = (
    _: string | string[] | InputFile | InputFile[] | null
  ) => {};

  propagateTouched = () => {};
  get actionUrl(): string {
    const query = new URLSearchParams(
      ObjectUtil.removeEmptyProperties({
        bucket: this.bucket,
        documentType: this.documentType || '',
      })
    );
    return `${this.uploadUrl}?${query}`;
  }

  handlePreview = async (file: NzUploadFile): Promise<void> => {
    if (!file.url && !file['preview']) {
      file['preview'] = await getBase64(file.originFileObj);
    }
    this.previewImage = file.url || file['preview'];
    this.previewVisible = true;
  };
  constructor(
    private fileService: FileService,
    private cdr: ChangeDetectorRef,
    private config: Configuration
  ) {}

  ngOnInit(): void {
    this.uploadUrl = this.config.apiFileUrl + '/bucket';
    if (!this.accept && this.uploadType === 'photo')
      this.accept = '.jpg, .png, .jepg, .svg';
  }
  setInitialValue(
    value?: InputFile | string | Array<InputFile | string> | null
  ) {
    if (!value) {
      this.list = [];
      this.fileList = new Map();
      return;
    }
    // if (this.fileList) return;
    if (!this.fileList || !this.multiple) this.fileList = new Map();
    if (typeof value === 'string') {
      const v = {
        name: StringUtil.getFileNameFromUrl(value),
        src: value,
      };
      this.fileList.set(value, v);
    } else if (typeof value === 'object' && !Array.isArray(value) && value.src)
      this.fileList.set(value.src, {
        ...value,
        name: value.name || StringUtil.getFileNameFromUrl(value.src),
      });
    else if (Array.isArray(value) && value.length > 0) {
      value.forEach((v) => {
        if (!v) return;
        if (typeof v === 'string')
          return this.fileList.set(v, {
            name: StringUtil.getFileNameFromUrl(v),
            src: v,
          });
        return this.fileList.set(v.src, {
          ...v,
          name: v.name || StringUtil.getFileNameFromUrl(v.src),
        });
      });
    }
  }
  writeValue(
    value?: InputFile | string | Array<InputFile | string> | null
  ): void {
    this.setInitialValue(value);
    this.cdr.detectChanges();
  }
  private propagete() {
    if (this.multiple) {
      if (this.fileValue === 'full')
        this.propagateChange([...this.fileList].map(([_, f]) => f));
      else this.propagateChange([...this.fileList].map(([_, f]) => f?.src));
    } else {
      if (this.fileValue === 'full')
        this.propagateChange([...this.fileList.values()][0]);
      else this.propagateChange([...this.fileList.values()][0]?.src);
    }
    this.cdr.detectChanges();
  }
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  change($event: NzUploadChangeParam) {
    if ($event.type !== 'success') return;

    if (this.multiple) {
      const files = $event.fileList.map((f) => f.response);
      this.setInitialValue(files);
    } else {
      this.setInitialValue($event.file.response);
    }
    this.propagete();
  }
  delete(file?: string) {
    if (!file) return;
    this.fileList.delete(file);
    this.propagete();
  }
  updateDocumentType(file: InputFile, documentType: APPLICATION_DOCUMENT_TYPE) {
    this.fileService.update(file.src, { documentType }).subscribe((res) => {
      this.fileList.set(res.src, res);
      this.propagete();
    });
  }
}
